import { ThumbnailUniDriver } from '@wix/design-system/dist/testkit/unidriver';
import type { GetOptions, Testkit, UniDriver } from 'ricos-types/testkit';
import { dataHook, RicosPluginToolbarNames } from 'wix-rich-content-common';
import { INSERT_PLUGIN_BUTTONS } from 'wix-rich-content-editor-common';

import {
  IMAGE_AND_LIST_OPTION,
  IMAGE_GRID_OPTION,
  POLLS_ADD_ANSWER_BUTTON,
  POLLS_ANSWER_IMAGE,
  POLLS_ANSWER_ITEMS,
  POLLS_ANSWER_TEXT_INPUT,
  POLLS_DROPDOWN_CUSTOMIZE_LAYOUT,
  POLLS_ITEM_PERCENTAGE_PREVIEW,
  POLLS_MULTIPLE_ANSWER_TEXT,
  POLLS_QUESTION_IMAGE,
  POLLS_QUESTION_TEXT_INPUT,
  POLLS_REMOVE_ANSWER_BUTTON,
  PRESET_SELECTOR_MODAL,
  ROOT,
  TEXT_LIST_OPTION,
} from '../dataHooks';
import { toolbarsToAddIn } from '../getAddButtons';
import type { PollsSettingsUniDriver } from './social-polls-settings-tab.unidriver';
import { createPollsSettingsUniDriver } from './social-polls-settings-tab.unidriver';

export type PollsTestkit = Testkit<{
  content: {
    atIndex: (index: number) => {
      exists: (options?: GetOptions) => Promise<boolean>;
      clickOnPoll: () => Promise<void>;
      multipleVotesTextExists: () => Promise<boolean>;
      getDirection: () => Promise<string | null>;
      getStyle: () => Promise<string | null>;
      questionImageExists: () => Promise<boolean>;
      answerImageExist: (index: number) => Promise<boolean>;
      editor: {
        setQuestionText: (text: string) => Promise<void>;
        setAnswerText: (index: number, text: string) => Promise<void>;
        addAnswer: () => Promise<void>;
        removeAnswer: (index: number) => Promise<void>;
      };
      preview: {
        getQuestionText: () => Promise<string>;
        getAnswerText: (index: number) => Promise<string>;
        countAnswers: () => Promise<number>;
        votePercentageExists: (index: number) => Promise<boolean>;
      };
    };
  };
  presetSelectorModal: {
    waitForElement: () => Promise<void>;
    simpleListThumbnail: ReturnType<typeof ThumbnailUniDriver>;
    imageAndListThumbnail: ReturnType<typeof ThumbnailUniDriver>;
    imageGridThumbnail: ReturnType<typeof ThumbnailUniDriver>;
  };
  settings: PollsSettingsUniDriver;
  toolbars: {
    createAddButtonToolbars: (getAddButtonToolbarBase: () => UniDriver<Element>) => {
      click: () => Promise<void>;
      toolbarsToAddIn: typeof toolbarsToAddIn;
    };
    createPluginToolbar: (
      getPluginToolbarBase: (pluginType: typeof RicosPluginToolbarNames.POLL) => UniDriver<Element>
    ) => {
      clickDelete: () => Promise<void>;
      exists: () => Promise<boolean>;
      clickLayoutSelectionDropdown: () => Promise<void>;
      layoutSelectionDropdown: {
        waitForElement: () => Promise<boolean>;
        clickCustomizeLayout: () => Promise<void>;
        getSelectedLayout: () => Promise<string>;
      };
      clickDesign: () => Promise<void>;
      clickSettings: () => Promise<void>;
    };
  };
}>;

export const createPollsTestkit: PollsTestkit = getBase => {
  const getPoll = async (index: number, options?: GetOptions) => {
    const polls = await getBase().getAll(dataHook(ROOT), options);
    return polls.at(index);
  };

  return {
    content: {
      atIndex: index => {
        return {
          exists: async (options?: GetOptions) => {
            try {
              const poll = await getPoll(index, options);
              return poll.exists();
            } catch (error) {
              return false;
            }
          },
          clickOnPoll: async () => (await getPoll(index)).click(),
          getDirection: async () => {
            const poll = await getPoll(index);
            return poll.attr('dir');
          },
          getStyle: async () => {
            const poll = await getPoll(index);
            return poll.attr('style');
          },
          questionImageExists: async () => {
            try {
              const image = await getBase().get(dataHook(POLLS_QUESTION_IMAGE));
              return image.exists();
            } catch (error) {
              return false;
            }
          },
          answerImageExist: async index => {
            try {
              const images = await getBase().getAll(dataHook(POLLS_ANSWER_IMAGE));
              return images.at(index).exists();
            } catch (error) {
              return false;
            }
          },
          multipleVotesTextExists: async () => {
            try {
              const text = await getBase().get(dataHook(POLLS_MULTIPLE_ANSWER_TEXT));
              return text.exists();
            } catch (error) {
              return false;
            }
          },
          editor: {
            setQuestionText: async text => {
              const questionInput = await getBase().get(dataHook(POLLS_QUESTION_TEXT_INPUT));
              await questionInput.enterValue(text);
            },
            setAnswerText: async (index, text) => {
              const answerInput = await getBase().getAll(dataHook(POLLS_ANSWER_TEXT_INPUT));
              await answerInput.at(index).enterValue(text);
            },
            addAnswer: async () => {
              const button = await getBase().get(dataHook(POLLS_ADD_ANSWER_BUTTON));
              await button.click();
            },
            removeAnswer: async index => {
              const answerInput = await getBase().getAll(dataHook(POLLS_ANSWER_TEXT_INPUT));
              await answerInput.at(index).click();
              const button = await getBase().getAll(dataHook(POLLS_REMOVE_ANSWER_BUTTON));
              await button.at(index).click();
            },
          },
          preview: {
            getQuestionText: async () => {
              const question = await getBase().get(dataHook(POLLS_QUESTION_TEXT_INPUT));
              return question.text();
            },
            getAnswerText: async index => {
              const answers = await getBase().getAll(dataHook(POLLS_ANSWER_TEXT_INPUT));
              return answers.at(index).text();
            },
            countAnswers: async () => {
              const items = await getBase().getAll(dataHook(POLLS_ANSWER_ITEMS));
              return items.count();
            },
            votePercentageExists: async index => {
              try {
                const items = await getBase().getAll(dataHook(POLLS_ITEM_PERCENTAGE_PREVIEW));
                return items.at(index).exists();
              } catch (error) {
                return false;
              }
            },
          },
        };
      },
    },
    presetSelectorModal: {
      waitForElement: async () => {
        await getBase().get(dataHook(PRESET_SELECTOR_MODAL));
      },
      simpleListThumbnail: ThumbnailUniDriver(getBase().$(dataHook(TEXT_LIST_OPTION)), getBase()),
      imageAndListThumbnail: ThumbnailUniDriver(
        getBase().$(dataHook(IMAGE_AND_LIST_OPTION)),
        getBase()
      ),
      imageGridThumbnail: ThumbnailUniDriver(getBase().$(dataHook(IMAGE_GRID_OPTION)), getBase()),
    },
    settings: createPollsSettingsUniDriver(getBase),
    toolbars: {
      createAddButtonToolbars: getAddButtonToolbarBase => ({
        click: async () => {
          const pollsPluginButton = await getAddButtonToolbarBase().get(
            dataHook(INSERT_PLUGIN_BUTTONS.POLLS)
          );
          await pollsPluginButton.click();
        },
        toolbarsToAddIn,
      }),
      createPluginToolbar: getPluginToolbarBase => ({
        clickDelete: async () => {
          const deleteButton = await getPluginToolbarBase(RicosPluginToolbarNames.POLL).get(
            dataHook('blockButton_delete')
          );
          return deleteButton.click();
        },
        exists: async () => {
          try {
            return (await getPluginToolbarBase(RicosPluginToolbarNames.POLL)).exists();
          } catch (error) {
            return false;
          }
        },
        clickLayoutSelectionDropdown: async () => {
          const settings = await getBase().get(dataHook('baseToolbarButton_layout'));
          await settings.click();
        },
        layoutSelectionDropdown: {
          waitForElement: async () => {
            try {
              const dropdown = await getBase().get(dataHook('dropdown-modal-options-wrapper'));
              return dropdown.exists();
            } catch (error) {
              return false;
            }
          },
          clickCustomizeLayout: async () => {
            const dropdown = await getBase().get(dataHook('dropdown-modal-options-wrapper'));
            const button = await dropdown.get(dataHook(POLLS_DROPDOWN_CUSTOMIZE_LAYOUT));
            await button.click();
          },
          getSelectedLayout: async () => {
            const dropdown = await getBase().get(dataHook('dropdown-modal-options-wrapper'));
            const selectedLayout = await dropdown.get(`[data-selected="true"]`);
            return selectedLayout.text();
          },
        },
        clickDesign: async () => {
          const button = await getBase().get(dataHook('baseToolbarButton_design'));
          await button.click();
        },
        clickSettings: async () => {
          const button = await getBase().get(dataHook('baseToolbarButton_settings'));
          await button.click();
        },
      }),
    },
  };
};
